import React, { useState, useEffect } from 'react';
import { supplierAPI, billingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import KenyaLocationFields from '../../components/common/KenyaLocationFields';
import BusinessTypePicker from '../../components/common/BusinessTypePicker';
import { normalizeBusinessTypes } from '../../constants/businessTypes';
import toast from 'react-hot-toast';
import {
  BuildingStorefrontIcon,
  PhoneIcon,
  MapPinIcon,
  TruckIcon,
  BanknotesIcon,
  PhotoIcon,
  BellIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const supplierToForm = (supplier) => {
  const user = supplier?.user || {};
  return {
    businessName: supplier.businessName || supplier.business_name || '',
    businessTypes: normalizeBusinessTypes(supplier),
    description: supplier.description || '',
    contactPhone: supplier.contactPhone || supplier.contact_phone || user.phone || '',
    contactWhatsapp: supplier.contactWhatsapp || supplier.contact_whatsapp || '',
    contactEmail: supplier.contactEmail || supplier.contact_email || user.email || '',
    businessAddress: supplier.businessAddress || supplier.business_address || '',
    county: supplier.county || '',
    subCounty: supplier.subCounty || supplier.sub_county || '',
    town: supplier.town || '',
    deliveryAvailable: supplier.deliveryAvailable ?? supplier.delivery_available ?? true,
    deliveryRadius: supplier.deliveryRadius ?? supplier.delivery_radius ?? '',
    deliveryFee: supplier.deliveryFee ?? supplier.delivery_fee ?? '',
    pickupAvailable: supplier.pickupAvailable ?? supplier.pickup_available ?? true,
    pickupAddress: supplier.pickupAddress || supplier.pickup_address || '',
    settlementPhone: supplier.settlementPhone || supplier.settlement_phone || user.phone || '',
    settlementMethod: supplier.settlementMethod || supplier.settlement_method || 'mpesa',
    settlementBankName: supplier.settlementBankName || supplier.settlement_bank_name || '',
    settlementBankAccount: supplier.settlementBankAccount || supplier.settlement_bank_account || '',
    settlementInterval: supplier.settlementInterval || supplier.settlement_interval || 'weekly',
    notifyOnNewOrder: supplier.metadata?.notifyOnNewOrder ?? true,
    notifyOnOrderStatusChange: supplier.metadata?.notifyOnOrderStatusChange ?? true,
    notifyOnPaymentReceived: supplier.metadata?.notifyOnPaymentReceived ?? true
  };
};

const SupplierSettings = () => {
  const { updateLocalUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [paymentPhone, setPaymentPhone] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    businessTypes: [],
    description: '',
    contactPhone: '',
    contactWhatsapp: '',
    contactEmail: '',
    businessAddress: '',
    county: '',
    subCounty: '',
    town: '',
    deliveryAvailable: true,
    deliveryRadius: '',
    deliveryFee: '',
    pickupAvailable: true,
    pickupAddress: '',
    settlementPhone: '',
    settlementMethod: 'mpesa',
    settlementBankName: '',
    settlementBankAccount: '',
    settlementInterval: 'weekly',
    // Notification preferences (order-related only for suppliers)
    notifyOnNewOrder: true,
    notifyOnOrderStatusChange: true,
    notifyOnPaymentReceived: true
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const profileRes = await supplierAPI.getProfile();
      const supplier = profileRes.data.supplier;
      if (supplier) {
        setProfile(supplier);
        setFormData(supplierToForm(supplier));
        setPaymentPhone(supplier.user?.phone || supplier.contactPhone || '');
        if (supplier.logoUrl || supplier.businessName) {
          updateLocalUser({
            ...(supplier.logoUrl ? { avatarUrl: supplier.logoUrl } : {}),
            ...(supplier.businessName ? { businessName: supplier.businessName } : {})
          });
        }
      }
    } catch (error) {
      console.error('Load profile error:', error);
      toast.error(error.response?.data?.message || 'Failed to load business settings');
    } finally {
      try {
        const subRes = await billingAPI.getStatus();
        setSubscription(subRes.data.subscription);
      } catch (_) {}
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.businessTypes.length) {
      toast.error('Select at least one business type');
      return;
    }
    setSaving(true);
    try {
      const { notifyOnNewOrder, notifyOnOrderStatusChange, notifyOnPaymentReceived, businessTypes, ...restFormData } = formData;

      const cleanedData = {};
      Object.entries(restFormData).forEach(([key, value]) => {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          cleanedData[key] = trimmed === '' ? null : trimmed;
        } else {
          cleanedData[key] = value;
        }
      });

      const data = {
        ...cleanedData,
        businessTypes,
        businessType: businessTypes[0],
        deliveryRadius: formData.deliveryRadius === '' || formData.deliveryRadius == null ? null : Number(formData.deliveryRadius),
        deliveryFee: formData.deliveryFee === '' || formData.deliveryFee == null ? 0 : Number(formData.deliveryFee),
        metadata: {
          notifyOnNewOrder,
          notifyOnOrderStatusChange,
          notifyOnPaymentReceived
        }
      };

      const response = await supplierAPI.updateProfile(data);
      const supplier = response.data?.supplier;
      if (supplier) {
        setProfile((prev) => ({ ...prev, ...supplier, user: supplier.user || prev?.user }));
        setFormData(supplierToForm({ ...supplier, user: supplier.user || profile?.user }));
      }
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.details || error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await supplierAPI.uploadLogo(file);
      if (data?.url) updateLocalUser({ avatarUrl: data.url });
      toast.success('Logo uploaded');
      loadProfile(false);
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleSubscription = async () => {
    if (!paymentPhone) {
      toast.error('Please enter your M-Pesa phone number');
      return;
    }
    try {
      setProcessingPayment(true);
      await billingAPI.initiateStk(paymentPhone);
      toast.success('Payment request sent! Check your phone for M-Pesa prompt.');
      
      // Poll for payment confirmation
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const res = await billingAPI.getStatus();
          if (res.data.subscription?.status === 'active') {
            clearInterval(pollInterval);
            setSubscription(res.data.subscription);
            toast.success('Subscription activated successfully!');
            setProcessingPayment(false);
          }
        } catch (e) {
          console.error('Subscription check error:', e);
        }
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setProcessingPayment(false);
        }
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  // Check if subscription is active
  const isSubscriptionActive = () => {
    if (!subscription) return false;
    const endsAt = new Date(subscription.endsAt);
    return endsAt > new Date() && subscription.status !== 'expired';
  };

  // Get subscription status display
  const getSubscriptionStatus = () => {
    if (!subscription) return { status: 'none', label: 'No Subscription', color: 'red' };
    
    const endsAt = new Date(subscription.endsAt);
    const now = new Date();
    const daysLeft = Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24));
    
    if (subscription.status === 'expired' || endsAt <= now) {
      return { status: 'expired', label: 'Expired', color: 'red', daysLeft: 0 };
    }
    if (daysLeft <= 7) {
      return { status: 'expiring', label: `Expires in ${daysLeft} days`, color: 'yellow', daysLeft };
    }
    return { status: 'active', label: 'Active', color: 'green', daysLeft };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Business Settings</h1>
        <p className="text-gray-600">Manage your supplier profile and preferences</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Profile Info (Read-only display) */}
        {profile?.user && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-sm border-2 border-white">
                {(profile.logoUrl || profile.user.avatarUrl) ? (
                  <img src={profile.logoUrl || profile.user.avatarUrl} alt="Business logo" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                    {(formData.businessName?.[0] || profile.user.firstName?.[0] || profile.user.email?.[0] || 'S').toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {formData.businessName
                    || (profile.user.firstName || profile.user.lastName
                      ? `${profile.user.firstName || ''} ${profile.user.lastName || ''}`.trim()
                      : 'Supplier Account')}
                </h2>
                <p className="text-sm text-gray-600">{profile.user.email}</p>
                {profile.user.phone && <p className="text-sm text-gray-500">{profile.user.phone}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Logo Upload */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <PhotoIcon className="h-5 w-5 mr-2 text-gray-500" />
            Business Logo
          </h3>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
              {profile?.logoUrl ? (
                <img src={profile.logoUrl} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <BuildingStorefrontIcon className="h-12 w-12 text-gray-300" />
              )}
            </div>
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              <span className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                Upload Logo
              </span>
            </label>
          </div>
        </div>

        {/* Business Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <BuildingStorefrontIcon className="h-5 w-5 mr-2 text-gray-500" />
            Business Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
              <input
                type="text"
                name="businessName"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.businessName}
                onChange={handleChange}
              />
            </div>
            <div className="md:col-span-2">
              <BusinessTypePicker
                value={formData.businessTypes}
                onChange={(businessTypes) => setFormData((prev) => ({ ...prev, businessTypes }))}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.description}
                onChange={handleChange}
                placeholder="Tell farmers about your business..."
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <PhoneIcon className="h-5 w-5 mr-2 text-gray-500" />
            Contact Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                name="contactPhone"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.contactPhone}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
              <input
                type="tel"
                name="contactWhatsapp"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.contactWhatsapp}
                onChange={handleChange}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="contactEmail"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.contactEmail}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <MapPinIcon className="h-5 w-5 mr-2 text-gray-500" />
            Location
          </h3>
          <KenyaLocationFields
            county={formData.county}
            subCounty={formData.subCounty}
            town={formData.town}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
          />
          <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
              <textarea
                name="businessAddress"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.businessAddress}
                onChange={handleChange}
              />
          </div>
        </div>

        {/* Delivery & Pickup */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <TruckIcon className="h-5 w-5 mr-2 text-gray-500" />
            Delivery & Pickup
          </h3>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="deliveryAvailable"
                checked={formData.deliveryAvailable}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Offer delivery</span>
            </label>
            {formData.deliveryAvailable && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Radius (km)</label>
                  <input
                    type="number"
                    name="deliveryRadius"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.deliveryRadius}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Fee (KES)</label>
                  <input
                    type="number"
                    name="deliveryFee"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.deliveryFee}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
            <label className="flex items-center">
              <input
                type="checkbox"
                name="pickupAvailable"
                checked={formData.pickupAvailable}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Allow pickup</span>
            </label>
            {formData.pickupAvailable && (
              <div className="pl-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address</label>
                <input
                  type="text"
                  name="pickupAddress"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.pickupAddress}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>
        </div>

        {/* Settlement */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <BanknotesIcon className="h-5 w-5 mr-2 text-gray-500" />
            Settlement Preferences
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Method</label>
              <select
                name="settlementMethod"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.settlementMethod}
                onChange={handleChange}
              >
                <option value="mpesa">M-Pesa</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>
            {formData.settlementMethod === 'mpesa' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">M-Pesa Phone</label>
                <input
                  type="tel"
                  name="settlementPhone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.settlementPhone}
                  onChange={handleChange}
                />
              </div>
            )}
            {formData.settlementMethod === 'bank' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    name="settlementBankName"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.settlementBankName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input
                    type="text"
                    name="settlementBankAccount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.settlementBankAccount}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Subscription */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-xl shadow-lg p-6 text-white relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative">
            <h3 className="font-bold text-lg mb-4 flex items-center">
              <CreditCardIcon className="h-6 w-6 mr-2" />
              Subscription Status
            </h3>
            
            {/* Current Status */}
            {(() => {
              const subStatus = getSubscriptionStatus();
              return (
                <div className={`p-4 rounded-xl mb-6 ${
                  subStatus.color === 'green' ? 'bg-green-500/20 border border-green-400/30' :
                  subStatus.color === 'yellow' ? 'bg-yellow-500/20 border border-yellow-400/30' :
                  'bg-red-500/20 border border-red-400/30'
                }`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      {subStatus.color === 'green' ? (
                        <div className="p-2 bg-green-400/30 rounded-full">
                          <CheckCircleIcon className="h-6 w-6 text-green-300" />
                        </div>
                      ) : (
                        <div className={`p-2 rounded-full ${subStatus.color === 'yellow' ? 'bg-yellow-400/30' : 'bg-red-400/30'}`}>
                          <ExclamationTriangleIcon className={`h-6 w-6 ${
                            subStatus.color === 'yellow' ? 'text-yellow-300' : 'text-red-300'
                          }`} />
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-lg">{subStatus.label}</p>
                        {subscription?.endsAt && subStatus.status !== 'none' && (
                          <p className="text-sm text-white/70">
                            {subStatus.status === 'expired' 
                              ? `Expired on ${new Date(subscription.endsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                              : `Valid until ${new Date(subscription.endsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                    {subStatus.status === 'active' && subStatus.daysLeft && (
                      <div className="text-right">
                        <p className="text-3xl font-bold">{subStatus.daysLeft}</p>
                        <p className="text-xs text-white/70">days left</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Progress bar for active subscriptions */}
                  {subscription?.startsAt && subscription?.endsAt && subStatus.status !== 'none' && subStatus.status !== 'expired' && (
                    <div className="mt-4">
                      <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-white/60 rounded-full transition-all"
                          style={{ 
                            width: `${Math.max(0, Math.min(100, ((new Date() - new Date(subscription.startsAt)) / (new Date(subscription.endsAt) - new Date(subscription.startsAt))) * 100))}%` 
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Features List */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                'Unlimited Products',
                'Order Management',
                'Customer Notifications',
                'Settlement Tracking',
                'Analytics Dashboard',
                'Priority Support'
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <div className="p-0.5 bg-green-400/30 rounded-full">
                    <CheckCircleIcon className="h-4 w-4 text-green-300" />
                  </div>
                  <span className="text-white/90">{feature}</span>
                </div>
              ))}
            </div>

            {/* Pricing */}
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/70">Monthly Plan</p>
                  <p className="text-2xl font-bold">KES 1,000<span className="text-sm font-normal text-white/70">/month</span></p>
                </div>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/M-PESA_LOGO-01.svg/1200px-M-PESA_LOGO-01.svg.png" 
                  alt="M-Pesa" 
                  className="h-8 object-contain bg-white/90 rounded px-2 py-1"
                />
              </div>
            </div>

            {/* Payment Form */}
            {(!isSubscriptionActive() || getSubscriptionStatus().daysLeft <= 14) && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-1">
                    {isSubscriptionActive() ? 'Renew via M-Pesa' : 'Subscribe via M-Pesa'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+254</span>
                    <input
                      type="tel"
                      placeholder="7XXXXXXXX"
                      value={paymentPhone.replace(/^254/, '').replace(/^\+254/, '').replace(/^0/, '')}
                      onChange={(e) => setPaymentPhone('254' + e.target.value.replace(/\D/g, '').slice(0, 9))}
                      className="w-full pl-14 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSubscription}
                  disabled={processingPayment}
                  className="w-full px-4 py-3 bg-white text-blue-700 rounded-lg hover:bg-white/90 disabled:opacity-50 font-bold transition-colors flex items-center justify-center gap-2"
                >
                  {processingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-700"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="h-5 w-5" />
                      {isSubscriptionActive() ? 'Renew Now - KES 1,000' : 'Subscribe Now - KES 1,000'}
                    </>
                  )}
                </button>
                
                {processingPayment && (
                  <p className="text-sm text-white/70 text-center">
                    Check your phone for the M-Pesa prompt
                  </p>
                )}
              </div>
            )}

            {/* Fully active message */}
            {isSubscriptionActive() && getSubscriptionStatus().daysLeft > 14 && (
              <div className="text-center py-2">
                <p className="text-white/80 text-sm">Your subscription is active and running smoothly!</p>
              </div>
            )}
          </div>
        </div>

        {/* Order Notifications */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <BellIcon className="h-5 w-5 mr-2 text-gray-500" />
            Order Notifications
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Get notified when farmers place orders for your products
          </p>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="notifyOnNewOrder"
                checked={formData.notifyOnNewOrder}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">
                <span className="font-medium">New Orders</span>
                <span className="block text-gray-500">Get notified when a farmer places a new order</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="notifyOnOrderStatusChange"
                checked={formData.notifyOnOrderStatusChange}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">
                <span className="font-medium">Order Updates</span>
                <span className="block text-gray-500">Get notified when order status changes</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="notifyOnPaymentReceived"
                checked={formData.notifyOnPaymentReceived}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-3 text-sm text-gray-700">
                <span className="font-medium">Payment Received</span>
                <span className="block text-gray-500">Get notified when payment is received for an order</span>
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupplierSettings;
